import React, { useState, useEffect, useRef } from 'react';
import TermAutocomplete from '../../comun/TermAutocomplete';

import api from '../../services/api';
import { savePendingTransaction } from '../../servicios/offlineSync';
import Modal from '../../comun/Modal';
import ConfirmModal from '../../comun/ConfirmModal';
import { HojaEstilos, HojaDatalists, HojaEncabezado, Linea, RxFinalTabla, EspecificacionesLentes, exportarHojaPDF, ErroresHoja, AvisoErrores } from './HojaVisualFormato';
import { validarFormula, normalizarCampo } from './optometria';
import ModalFactura from './ModalFactura';

const defaultRx = {
    esfera_od: '', cilindro_od: '', eje_od: '', add_od: '', avl_od: '20/20', avc_od: '20/20',
    esfera_oi: '', cilindro_oi: '', eje_oi: '', add_oi: '', avl_oi: '20/20', avc_oi: '20/20',
    tipo_lentes: 'Monofocal',
    color: '',
    bifocal: '',
    tratamiento: 'Filtro Azul (Blue Block UV420)',
    dp: '',
    alt: '',
    diam: '',
    observaciones: ''
};

const Consultas = () => {
    const [quotes, setQuotes] = useState([]);
    const [searchQuote, setSearchQuote] = useState('');
    const [currentQuote, setCurrentQuote] = useState(null);
    const [saveModal, setSaveModal] = useState({ show: false, message: '', type: 'online' });
    const [deleteConfirm, setDeleteConfirm] = useState({ show: false, id: null });
    const [duplicateQuoteAlert, setDuplicateQuoteAlert] = useState({ show: false, existingQuote: null, pendingSaveData: null });
    const [showDrawer, setShowDrawer] = useState(false);

    const defaultForm = {
        nombre: '',
        cedula: '',
        representante: '',
        cedulaRepresentante: '',
        fechaNacimiento: '',
        direccion: '',
        telefono: '',
        correo: '',
        ocupacion: '',
        discountPercentage: 0
    };

    const [form, setForm] = useState(defaultForm);
    const [rxData, setRxData] = useState(defaultRx);
    const [treatments, setTreatments] = useState([{ od: '', procedimiento: '', cantidad: 1, precio: 0, total_final: 0 }]);
    const [servicios, setServicios] = useState([]);
    const [supplies, setSupplies] = useState([]);
    const [procedures, setProcedures] = useState([]);

    const [intentoGuardarRx, setIntentoGuardarRx] = useState(false);
    const handleRxChange = (e) => {
        const { name, value } = e.target;
        setRxData(prev => ({ ...prev, [name]: value }));
    };

    const handleRxBlur = (e) => {
        const { name, value } = e.target || {};
        if (!name || !(name in rxData)) return;
        const normalizado = normalizarCampo(name, value);
        if (normalizado !== value) setRxData(prev => ({ ...prev, [name]: normalizado }));
    };

    // Copia el RX final del último examen visual del paciente para no transcribir la fórmula a mano.
    const [loadingRx, setLoadingRx] = useState(false);
    const handleTraerRxUltimoExamen = async () => {
        setLoadingRx(true);
        try {
            let patientId = currentQuote?.db_patient_id || linkedPatientId;
            if (!patientId && form.cedula) {
                const res = await api.get(`/api/patients/?search=${encodeURIComponent(form.cedula)}`);
                const found = (res.data.results || res.data || []).find(p => p.cedula === form.cedula);
                patientId = found?.id;
            }
            if (!patientId) {
                setSaveModal({ show: true, message: 'Ingrese la cédula de un paciente registrado para buscar su último examen.', type: 'error' });
                return;
            }
            const res = await api.get(`/api/clinical-records/visual-records/?patient=${patientId}`);
            const recs = res.data.results || res.data || [];
            const ultimo = [...recs].filter(r => !r.anulada).sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at))[0];
            if (!ultimo) {
                setSaveModal({ show: true, message: 'Este paciente no tiene exámenes visuales registrados.', type: 'error' });
                return;
            }
            setRxData(prev => {
                const next = { ...prev };
                Object.keys(defaultRx).forEach(k => {
                    if (ultimo[k] !== undefined && ultimo[k] !== null) next[k] = ultimo[k];
                });
                return next;
            });
        } catch (error) {
            setSaveModal({ show: true, message: `No se pudo cargar el último examen. ${error.message}`, type: 'error' });
        } finally {
            setLoadingRx(false);
        }
    };

    const loadQuotes = async () => {
        try {
            const resRecords = await api.get('/api/clinical-records/?limit=1000');
            const records = resRecords.data.results || resRecords.data;
            const quoteRecords = records.filter(r => 
                r.reason_for_consultation === 'Cotización / Presupuesto' || 
                r.reason_for_consultation === 'Cotización / Presupuesto (Completado)'
            );
            
            const resPatients = await api.get('/api/patients/?limit=1000');
            const patients = resPatients.data.results || resPatients.data;
            const patientMap = {};
            patients.forEach(p => patientMap[p.id] = p);

            const formattedQuotes = quoteRecords.map(r => {
                const p = patientMap[r.patient] || {};
                const quoteDate = new Date(r.updated_at || r.created_at);
                const savedDiscount = r.diagnostic_plans?.discountPercentage !== undefined 
                    ? r.diagnostic_plans.discountPercentage 
                    : (r.discountPercentage || 0);

                const extractedRx = r.odontogram_data?.rx || (r.odontogram_data?.esfera_od !== undefined ? r.odontogram_data : null) || r.diagnostic_plans?.rx || r.visual_record_data || {};

                return {
                    id: r.id,
                    db_patient_id: p.id,
                    status: r.anulada ? 'Anulada' : (r.reason_for_consultation.includes('Completado') ? 'Completada' : 'Borrador'),
                    anulada: !!r.anulada,
                    motivoAnulacion: r.motivo_anulacion || '',
                    date: quoteDate.toLocaleDateString(),
                    rawDate: quoteDate,
                    form: {
                        nombre: `${p.first_name || ''} ${p.last_name || ''}`.trim().toUpperCase(),
                        cedula: p.cedula || '',
                        representante: (p.representative || '').toUpperCase(),
                        cedulaRepresentante: p.representative_cedula || '',
                        fechaNacimiento: p.birth_date || '',
                        direccion: p.address || '',
                        telefono: p.phone || '',
                        correo: p.email || '',
                        ocupacion: p.occupation || '',
                        discountPercentage: savedDiscount
                    },
                    rxData: extractedRx,
                    odontogramaData: r.odontogram_data || {},
                    treatments: r.treatments ? r.treatments.map(t => ({...t, total_final: t.total_final !== undefined ? t.total_final : ((parseFloat(t.precio)||0)*(parseInt(t.cantidad)||1))})) : [{ od: '', procedimiento: '', cantidad: 1, precio: 0, total_final: 0 }]
                };
            });
            formattedQuotes.sort((a, b) => b.rawDate - a.rawDate);
            setQuotes(formattedQuotes);
        } catch (error) {
            console.error('Error cargando cotizaciones desde DB:', error);
        }
    };

    const loadCatalogs = async () => {
        try {
            const [resTreatments, resSupplies, resProcedures] = await Promise.allSettled([
                api.get('/api/treatments/?limit=1000'),
                api.get('/api/menu/supplies/?limit=1000'),
                api.get('/api/clinical-records/procedures/?limit=1000')
            ]);
            
            if (resTreatments.status === 'fulfilled') {
                setServicios(resTreatments.value.data.results || resTreatments.value.data || []);
            }
            if (resSupplies.status === 'fulfilled') {
                setSupplies(resSupplies.value.data.results || resSupplies.value.data || []);
            }
            if (resProcedures.status === 'fulfilled') {
                setProcedures(resProcedures.value.data.results || resProcedures.value.data || []);
            }
        } catch (error) {
            console.error('Error cargando catálogos para cotización:', error);
        }
    };

    useEffect(() => {
        loadQuotes();
        loadCatalogs();
    }, []);

    // Paciente sin presupuesto (llega desde Pacientes con ?patient_id=): se precargan sus datos y la fórmula de su último examen.
    const [linkedPatientId, setLinkedPatientId] = useState(null);
    useEffect(() => {
        const pId = new URLSearchParams(window.location.search).get('patient_id');
        if (!pId) return;
        (async () => {
            try {
                const { data: p } = await api.get(`/api/patients/${pId}/`);
                setLinkedPatientId(p.id);
                setForm(prev => ({
                    ...prev,
                    nombre: `${p.first_name || ''} ${p.last_name || ''}`.trim().toUpperCase(),
                    cedula: p.cedula || '',
                    representante: (p.representative || '').toUpperCase(),
                    cedulaRepresentante: p.representative_cedula || '',
                    fechaNacimiento: p.birth_date || '',
                    direccion: p.address || '',
                    telefono: p.phone || '',
                    correo: p.email || '',
                    ocupacion: p.occupation || ''
                }));
                const res = await api.get(`/api/clinical-records/visual-records/?patient=${p.id}`);
                const recs = res.data.results || res.data || [];
                const ultimo = [...recs].filter(r => !r.anulada).sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at))[0];
                if (ultimo) {
                    setRxData(prev => {
                        const next = { ...prev };
                        Object.keys(defaultRx).forEach(k => {
                            if (ultimo[k] !== undefined && ultimo[k] !== null) next[k] = ultimo[k];
                        });
                        return next;
                    });
                }
            } catch (error) {
                console.error('Error precargando paciente:', error);
            }
        })();
    }, []);

    const catalogOptions = React.useMemo(() => {
        // 1. Armazones, Lunas, Accesorios e Insumos del Inventario Óptico
        const supplyItems = (supplies || []).map(s => {
            const typeLabel = s.supply_type === 'armazon' ? 'Armazón' :
                              s.supply_type === 'luna' ? 'Luna / Cristal' :
                              s.supply_type === 'accesorio' ? 'Accesorio' : 'Insumo Óptico';
            
            const brandPart = s.brand ? `${s.brand} ` : '';
            const modelPart = s.model_code ? `[${s.model_code}] ` : '';
            const colorPart = s.color ? `- ${s.color} ` : '';
            const caliberPart = s.size_caliber ? `Cal. ${s.size_caliber} ` : '';
            const code = `${brandPart}${s.name} ${modelPart}${colorPart}${caliberPart}`.trim();
            const pvp = parseFloat(s.sale_price || s.cost_per_unit || 0);

            return {
                id: `supply_${s.id}`,
                code: code,
                description: `${typeLabel} | Stock: ${s.current_stock ?? 0} | PVP: $${pvp.toFixed(2)}`,
                price: pvp,
                type: typeLabel,
                category: 'armazon',
                raw: s
            };
        });

        // 2. Procedimientos Clínicos y Optométricos
        const procedureItems = (procedures || []).map(p => ({
            id: `proc_${p.id}`,
            code: p.name || p.procedimiento_recomendado || 'Procedimiento Optométrico',
            description: p.en_que_consiste || p.description || 'Procedimiento de evaluación visual',
            price: 0,
            type: 'Procedimiento',
            category: 'procedimiento',
            raw: p
        }));

        // 3. Servicios Clínicos
        const serviceItems = (servicios || []).map(s => {
            const pvp = parseFloat(s.price || 0);
            return {
                id: `srv_${s.id}`,
                code: s.name,
                description: s.description || `Servicio clínico | PVP: $${pvp.toFixed(2)}`,
                price: pvp,
                type: 'Servicio Clínico',
                category: 'servicio',
                raw: s
            };
        });

        return [...supplyItems, ...serviceItems, ...procedureItems];
    }, [supplies, servicios, procedures]);

    // Anular: el presupuesto no se borra, queda en el historial marcado como ANULADO.
    const handleAnularQuote = async () => {
        if (!currentQuote) return;
        const motivo = window.prompt('¿Anular este presupuesto?\n\nNO se borra: queda guardado y visible como ANULADO.\n\nEscriba el motivo:');
        if (motivo === null) return;
        if (!motivo.trim()) {
            setSaveModal({ show: true, message: 'Debe indicar el motivo de la anulación.', type: 'error' });
            return;
        }
        try {
            await api.post(`/api/clinical-records/${currentQuote.id}/anular/`, { motivo: motivo.trim() });
            setCurrentQuote({ ...currentQuote, anulada: true, status: 'Anulada', motivoAnulacion: motivo.trim() });
            setSaveModal({ show: true, message: 'Presupuesto anulado. Se conserva en el historial.', type: 'online' });
            loadQuotes();
        } catch (error) {
            setSaveModal({ show: true, message: `No se pudo anular. ${error.response?.data?.detail || error.message}`, type: 'error' });
        }
    };

    // Facturación electrónica del presupuesto: los productos pasan a la factura con el descuento global repartido.
    const [mostrarFactura, setMostrarFactura] = useState(false);
    const itemsParaFactura = () => {
        const pct = (parseFloat(form.discountPercentage) || 0) / 100;
        return treatments
            .filter(t => (t.procedimiento || '').trim() && (parseFloat(t.precio) || 0) > 0)
            .map(t => {
                const cantidad = parseInt(t.cantidad, 10) || 1;
                const precio = parseFloat(t.precio) || 0;
                // El IVA sale del servicio o producto del inventario con ese nombre (se configura en cada uno).
                const nombre = (t.procedimiento || '').trim().toLowerCase();
                const servicio = (servicios || []).find(x => (x.name || '').trim().toLowerCase() === nombre);
                const producto = !servicio && (supplies || []).find(x => (x.name || '').trim().toLowerCase() === nombre);
                const iva = (servicio || producto)?.iva ?? 15;
                const base = { descripcion: t.procedimiento, cantidad, precio, descuento: Math.round(cantidad * precio * pct * 100) / 100, iva };
                // Si es un producto del inventario, la factura descuenta su stock.
                return producto ? { ...base, producto_id: producto.id, stock: Number(producto.current_stock) || 0 } : base;
            });
    };
    const pacienteParaFactura = () => ({
        id: currentQuote?.db_patient_id || linkedPatientId || null,
        cedula: form.cedula, first_name: form.nombre, last_name: '',
        address: form.direccion, email: form.correo, phone: form.telefono
    });

    const handleNewQuote = () => {
        setCurrentQuote(null);
        setLinkedPatientId(null);
        setForm(defaultForm);
        setRxData(defaultRx);
        setTreatments([{ od: '', procedimiento: '', cantidad: 1, precio: 0, total_final: 0 }]);
    };

    const handleSelectQuote = (quote) => {
        setCurrentQuote(quote);
        setForm(quote.form || defaultForm);
        const existingRx = quote.rxData || quote.odontogramaData?.rx || (quote.odontogramaData?.esfera_od !== undefined ? quote.odontogramaData : null) || defaultRx;
        setRxData({ ...defaultRx, ...existingRx });
        setTreatments(quote.treatments ? quote.treatments.map(t => ({...t, total_final: t.total_final !== undefined ? t.total_final : ((parseFloat(t.precio)||0)*(parseInt(t.cantidad)||1))})) : [{ od: '', procedimiento: '', cantidad: 1, precio: 0, total_final: 0 }]);
    };

    const executeSave = async (patientData, recordData, quoteToUpdateId, patientIdOverride) => {
        if (!navigator.onLine) {
            savePendingTransaction(quoteToUpdateId ? 'UPDATE_QUOTE' : 'NEW_QUOTE', { patientData, recordData, quoteId: quoteToUpdateId, patientId: patientIdOverride });
            setSaveModal({ show: true, message: 'No hay conexión a internet, guardado en base de datos local', type: 'offline' });
            return;
        }

        try {
            let patientId = patientIdOverride;
            
            if (!patientId) {
                try {
                    const resPatient = await api.post('/api/patients/', patientData);
                    patientId = resPatient.data.id;
                } catch (err) {
                    if (err.response?.status === 400 && err.response.data?.cedula) {
                        const resSearch = await api.get(`/api/patients/?search=${patientData.cedula}`);
                        const searchResults = resSearch.data.results || resSearch.data || [];
                        if (searchResults.length > 0) {
                            patientId = searchResults[0].id;
                            await api.patch(`/api/patients/${patientId}/`, patientData);
                        } else {
                            throw err;
                        }
                    } else {
                        throw err;
                    }
                }
            } else {
                await api.patch(`/api/patients/${patientId}/`, patientData);
            }

            recordData.patient = patientId;

            if (quoteToUpdateId) {
                await api.patch(`/api/clinical-records/${quoteToUpdateId}/`, recordData);
            } else {
                await api.post('/api/clinical-records/', recordData);
            }

            setSaveModal({ show: true, message: 'Cliente guardado correctamente, guardado en la nube', type: 'online' });
            loadQuotes();
        } catch (error) {
            const errorMsg = error.response?.data ? JSON.stringify(error.response.data) : error.message;
            setSaveModal({ show: true, message: `Error al guardar: Verifique los datos. ${errorMsg}`, type: 'error' });
        }
    };

    const handleSaveCurrent = async () => {
        if (!form.nombre.trim()) {
            setSaveModal({ show: true, message: 'El nombre del paciente es obligatorio para guardar el presupuesto.', type: 'error' });
            return;
        }

        const { datos: rx, campos } = validarFormula(rxData);
        if (Object.keys(campos).length) {
            setIntentoGuardarRx(true);
            const primero = document.querySelector(`#print-quote-area [name="${Object.keys(campos)[0]}"]`);
            if (primero) {
                primero.scrollIntoView({ behavior: 'smooth', block: 'center' });
                primero.focus({ preventScroll: true });
            }
            return;
        }
        setIntentoGuardarRx(false);
        setRxData(rx);

        const patientData = {
            first_name: form.nombre.split(' ')[0] || 'Sin nombre',
            last_name: form.nombre.split(' ').slice(1).join(' ') || '',
            cedula: form.cedula || "",
            phone: form.telefono || '',
            address: form.direccion || '',
            email: form.correo || '',
            occupation: form.ocupacion || '',
            representative: form.representante || '',
            representative_cedula: form.cedulaRepresentante || '',
            birth_date: form.fechaNacimiento || null
        };

        const recordData = {
            patient: null, // Will be filled
            reason_for_consultation: 'Cotización / Presupuesto',
            odontogram_data: { rx, ...rx },
            visual_record_data: rx,
            diagnostic_plans: { discountPercentage: parseFloat(form.discountPercentage) || 0, rx },
            treatments: treatments
        };

        if (!currentQuote) {
            const existingQuote = quotes.find(q => 
                (form.cedula && q.form.cedula === form.cedula) || 
                (q.form.nombre.toLowerCase() === form.nombre.toLowerCase())
            );

            if (existingQuote) {
                setDuplicateQuoteAlert({
                    show: true,
                    existingQuote: existingQuote,
                    pendingSaveData: { patientData, recordData }
                });
                return;
            }
        }

        await executeSave(patientData, recordData, currentQuote?.id, currentQuote?.db_patient_id || linkedPatientId);
    };

    const [recovering, setRecovering] = useState(false);
    const [showAllQuotesModal, setShowAllQuotesModal] = useState(false);
    const [searchAllQuotes, setSearchAllQuotes] = useState('');

    const handleRecoverQuotes = async () => {
        if (!window.confirm("¿Seguro que deseas recuperar cotizaciones antiguas perdidas?")) return;
        setRecovering(true);
        try {
            const resRecords = await api.get('/api/clinical-records/?limit=1000');
            const allRecords = resRecords.data.results || resRecords.data;
            let recoveredCount = 0;
            const recoveredPatients = new Set();
            
            for (const record of allRecords) {
                // If it is NOT a quote currently
                if (record.reason_for_consultation !== 'Cotización / Presupuesto' && 
                    record.reason_for_consultation !== 'Cotización / Presupuesto (Completado)') {
                    
                    // Check if the patient ALREADY HAS A QUOTE in the system
                    const hasQuote = allRecords.some(r => 
                        (r.patient === record.patient || (r.patient && r.patient.id === (record.patient.id || record.patient))) && 
                        (r.reason_for_consultation === 'Cotización / Presupuesto' || r.reason_for_consultation === 'Cotización / Presupuesto (Completado)')
                    );

                    // Get the patient ID properly
                    const patientId = typeof record.patient === 'object' ? record.patient.id : record.patient;

                    if (!hasQuote && !recoveredPatients.has(patientId)) {
                        // Extract procedures from the clinical record to rebuild a quote
                        let quoteTreatments = [];
                        
                        if (record.treatments && record.treatments.length > 0) {
                            for (const t of record.treatments) {
                                if (!t || Object.keys(t).length === 0) continue;
                                
                                if (t.quote_prices && t.quote_prices.length > 0) {
                                    quoteTreatments.push(...t.quote_prices.map(q => ({
                                        procedimiento: q.name,
                                        cantidad: 1,
                                        precio: q.price,
                                        total_final: parseFloat(q.price) - parseFloat(q.discount || 0)
                                    })));
                                } else if (t.procedures) {
                                    quoteTreatments.push({
                                        procedimiento: t.procedures,
                                        cantidad: 1,
                                        precio: 0,
                                        total_final: 0
                                    });
                                } else if (t.service_ids || t.service_id) {
                                    const sIds = Array.isArray(t.service_ids) ? t.service_ids : [t.service_id];
                                    sIds.forEach(sId => quoteTreatments.push({ procedimiento: 'Tratamiento de historia', cantidad: 1, precio: 0, total_final: 0 }));
                                }
                            }
                        }
                        
                        if (quoteTreatments.length === 0) {
                            quoteTreatments.push({
                                procedimiento: 'Tratamiento recuperado',
                                cantidad: 1,
                                precio: 0,
                                total_final: 0
                            });
                        }

                        // Remove duplicates in quoteTreatments (if any)
                        const uniqueTreatments = Array.from(new Set(quoteTreatments.map(a => a.procedimiento)))
                            .map(proc => quoteTreatments.find(a => a.procedimiento === proc));

                        // Get the patient ID properly
                        const patientId = typeof record.patient === 'object' ? record.patient.id : record.patient;

                        const recoveredQuote = {
                            patient: patientId,
                            reason_for_consultation: 'Cotización / Presupuesto (Completado)',
                            odontogram_data: record.odontogram_data || {},
                            treatments: uniqueTreatments
                        };

                        await api.post('/api/clinical-records/', recoveredQuote);
                        recoveredPatients.add(patientId);
                        recoveredCount++;
                    }
                }
            }
            alert(`Se recuperaron ${recoveredCount} cotizaciones antiguas.`);
            loadQuotes();
        } catch (error) {
            console.error(error);
            const errorMsg = error.response?.data ? JSON.stringify(error.response.data) : error.message;
            alert('Error al recuperar cotizaciones. Detalles: ' + errorMsg);
        }
        setRecovering(false);
    };

    const confirmDelete = async () => {
        const id = deleteConfirm.id;
        setDeleteConfirm({ show: false, id: null });
        if (id) {
            try {
                if (!navigator.onLine) {
                    savePendingTransaction('DELETE_QUOTE', { quoteId: id });
                    setSaveModal({ show: true, message: 'No hay conexión a internet, la eliminación se sincronizará luego.', type: 'offline' });
                } else {
                    await api.delete(`/api/clinical-records/${id}/`);
                    setSaveModal({ show: true, message: 'Cotización eliminada correctamente.', type: 'online' });
                    loadQuotes();
                }
                if (currentQuote && currentQuote.id === id) {
                    handleNewQuote();
                }
            } catch (error) {
                console.error('Error al eliminar:', error);
                const errorMsg = error.response?.data ? JSON.stringify(error.response.data) : error.message;
                setSaveModal({ show: true, message: `Error al eliminar. ${errorMsg}`, type: 'error' });
            }
        }
    };

    const handleChangeForm = (e) => {
        let val = e.target.value;
        if (e.target.name === 'nombre' || e.target.name === 'representante') {
            val = val.toUpperCase();
        }
        setForm({ ...form, [e.target.name]: val });
    };

    const handleTreatmentChange = (index, field, value) => {
        const newTreatments = [...treatments];
        newTreatments[index][field] = value;
        
        // Auto calculate total_final if precio or cantidad is changed manually
        if (field === 'precio' || field === 'cantidad') {
            const precio = parseFloat(newTreatments[index].precio) || 0;
            const cantidad = parseInt(newTreatments[index].cantidad) || 1;
            newTreatments[index].total_final = precio * cantidad;
        }

        // Autocomplete price if a service is selected
        if (field === 'procedimiento') {
            const selectedService = servicios.find(s => s.name === value || `${s.name} - ${s.description}` === value);
            if (selectedService) {
                const precio = selectedService.price;
                const cantidad = parseInt(newTreatments[index].cantidad) || 1;
                newTreatments[index]['precio'] = precio;
                newTreatments[index]['procedimiento'] = selectedService.name;
                newTreatments[index]['total_final'] = precio * cantidad;
            }
        }
        
        setTreatments(newTreatments);
    };

    const addTreatmentRow = () => {
        setTreatments([...treatments, { od: '', procedimiento: '', cantidad: 1, precio: 0, total_final: 0 }]);
    };

    const removeTreatmentRow = (index) => {
        const newTreatments = treatments.filter((_, i) => i !== index);
        setTreatments(newTreatments.length ? newTreatments : [{ od: '', procedimiento: '', cantidad: 1, precio: 0, total_final: 0 }]);
    };

    const subtotalCost = treatments.reduce((acc, curr) => acc + ((parseFloat(curr.precio) || 0) * (parseInt(curr.cantidad) || 1)), 0);
    const globalDiscountAmount = (subtotalCost * (parseFloat(form.discountPercentage) || 0)) / 100;
    const finalTotalCost = subtotalCost - globalDiscountAmount;
    
    // Auto calculate if the manual total differs (legacy fallback, but we mainly use discountPercentage now)
    const manualTotalCost = treatments.reduce((acc, curr) => acc + (parseFloat(curr.total_final !== undefined ? curr.total_final : ((parseFloat(curr.precio)||0)*(parseInt(curr.cantidad)||1))) || 0), 0);

    const printRef = useRef(null);

    const handlePrint = async () => {
        try {
            await exportarHojaPDF(document.getElementById('print-quote-area'), `Presupuesto_${form.nombre || 'Paciente'}.pdf`);
        } catch (error) {
            console.error("Error generando PDF", error);
        }
    };

    const styles = {
        container: { padding: '2rem', backgroundColor: '#f8f9fb', minHeight: '100vh', display: 'flex', gap: '2rem' },
        sidebar: { width: '300px', backgroundColor: '#fff', borderRadius: '12px', padding: '1rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column' },
        main: { flex: 1, backgroundColor: '#fff', borderRadius: '12px', padding: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' },
        input: { padding: '0.5rem', borderRadius: '8px', border: '1px solid #cbd5e1', width: '100%', marginBottom: '1rem' },
        buttonPrimary: { padding: '0.75rem 1.5rem', borderRadius: '8px', backgroundColor: '#111827', color: '#fff', border: '1px solid #111827', fontWeight: '700', cursor: 'pointer' },
        buttonSuccess: { padding: '0.75rem 1.5rem', borderRadius: '8px', backgroundColor: 'transparent', border: '1.5px solid #10b981', color: '#10b981', fontWeight: '700', cursor: 'pointer' },
        buttonDanger: { padding: '0.5rem 1rem', borderRadius: '8px', backgroundColor: 'transparent', border: '1.5px solid #ef4444', color: '#ef4444', fontWeight: '700', cursor: 'pointer' },
        printOverlay: { position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 10000, overflowY: 'auto' }
    };

    return (
        <div style={{ backgroundColor: '#f8f9fb', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <Modal 
                isOpen={saveModal.show} 
                onClose={() => setSaveModal({ ...saveModal, show: false })} 
                title={saveModal.type === 'online' ? 'Guardado Exitoso' : saveModal.type === 'offline' ? 'Guardado Local (Sin Internet)' : 'Atención'}
                skipConfirm={true}
            >
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <i className={`bi ${saveModal.type === 'online' ? 'bi-cloud-check-fill' : saveModal.type === 'offline' ? 'bi-hdd-fill' : 'bi-exclamation-triangle-fill'}`} style={{ fontSize: '3rem', color: saveModal.type === 'online' ? '#10b981' : saveModal.type === 'offline' ? '#f59e0b' : '#ef4444' }}></i>
                    <h4 style={{ marginTop: '15px' }}>{saveModal.message}</h4>
                    {saveModal.type === 'offline' && <p style={{ color: '#666', marginTop: '10px' }}>La información se sincronizará automáticamente en segundo plano cuando vuelva la conexión.</p>}
                    <button style={{...styles.buttonPrimary, marginTop: '20px'}} onClick={() => setSaveModal({ ...saveModal, show: false })}>Entendido</button>
                </div>
            </Modal>

            <ConfirmModal 
                isOpen={deleteConfirm.show}
                onClose={() => setDeleteConfirm({ show: false, id: null })}
                onConfirm={confirmDelete}
                title="Eliminar Cotización"
                message="¿Está seguro de eliminar esta cotización? Esta acción no se puede deshacer."
            />

            {duplicateQuoteAlert.show && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '12px', maxWidth: '500px', width: '90%', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                        <h4 style={{ color: '#0f172a', fontWeight: 'bold', marginBottom: '1rem' }}><i className="bi bi-exclamation-circle text-warning me-2"></i>Paciente Existente</h4>
                        <p style={{ color: '#334155' }}>Esta es una consulta de un paciente que ya ha consultado antes (<strong>{duplicateQuoteAlert.existingQuote?.form?.nombre}</strong>).</p>
                        <p style={{ color: '#334155' }}>¿Deseas actualizar la consulta que ya está, o crear una nueva separada?</p>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '2rem' }}>
                            <button className="btn btn-light" onClick={() => setDuplicateQuoteAlert({ show: false, existingQuote: null, pendingSaveData: null })}>Cancelar</button>
                            <button className="btn btn-primary" onClick={() => {
                                const q = duplicateQuoteAlert.existingQuote;
                                setCurrentQuote(q);
                                setDuplicateQuoteAlert({ show: false, existingQuote: null, pendingSaveData: null });
                                executeSave(duplicateQuoteAlert.pendingSaveData.patientData, duplicateQuoteAlert.pendingSaveData.recordData, q.id, q.db_patient_id);
                            }}>Actualizar existente</button>
                            <button className="btn btn-success" onClick={() => {
                                const q = duplicateQuoteAlert.existingQuote;
                                setDuplicateQuoteAlert({ show: false, existingQuote: null, pendingSaveData: null });
                                executeSave(duplicateQuoteAlert.pendingSaveData.patientData, duplicateQuoteAlert.pendingSaveData.recordData, null, q.db_patient_id);
                            }}>Crear nueva</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal for All Quotes */}
            {showAllQuotesModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '12px', width: '90%', maxWidth: '1000px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '2px solid #f1f5f9', paddingBottom: '1rem' }}>
                            <h3 style={{ margin: 0, color: '#0f172a', fontWeight: 'bold' }}><i className="bi bi-list-check me-2 text-primary"></i>Listado de Todas las Cotizaciones</h3>
                            <button onClick={() => setShowAllQuotesModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#64748b' }}>&times;</button>
                        </div>
                        <div style={{ position: 'relative', marginBottom: '1rem' }}>
                            <i className="bi bi-search" style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}></i>
                            <input 
                                type="text" 
                                placeholder="Buscar paciente por nombre o cédula..." 
                                value={searchAllQuotes}
                                onChange={(e) => setSearchAllQuotes(e.target.value)}
                                className="form-control"
                                style={{ paddingLeft: '40px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                            />
                        </div>
                        <div style={{ overflowY: 'auto', flex: 1 }}>
                            <table className="table table-hover align-middle">
                                <thead className="table-light sticky-top">
                                    <tr>
                                        <th>Fecha</th>
                                        <th>Paciente</th>
                                        <th>Cédula</th>
                                        <th>Teléfono</th>
                                        <th>Estado</th>
                                        <th className="text-center">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {quotes.filter(q => 
                                        (q.form.nombre || '').toLowerCase().includes(searchAllQuotes.toLowerCase()) || 
                                        (q.form.cedula || '').includes(searchAllQuotes)
                                    ).map(q => (
                                        <tr key={q.id}>
                                            <td>{q.date}</td>
                                            <td style={{ fontWeight: 'bold' }}>{q.form.nombre || 'Sin nombre'}</td>
                                            <td>{q.form.cedula || '-'}</td>
                                            <td>{q.form.telefono || '-'}</td>
                                            <td>
                                                <span style={{
                                                    fontSize: '0.75rem',
                                                    padding: '4px 8px',
                                                    borderRadius: '12px',
                                                    fontWeight: 'bold',
                                                    backgroundColor: q.status === 'Completada' ? '#d1fae5' : q.status === 'Anulada' ? '#fee2e2' : '#fef3c7',
                                                    color: q.status === 'Completada' ? '#065f46' : q.status === 'Anulada' ? '#991b1b' : '#92400e',
                                                    border: `1px solid ${q.status === 'Completada' ? '#a7f3d0' : q.status === 'Anulada' ? '#fecaca' : '#fde68a'}`
                                                }}>
                                                    {q.status}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <button 
                                                    className="btn btn-sm btn-primary rounded-pill" 
                                                    onClick={() => {
                                                        handleSelectQuote(q);
                                                        setShowAllQuotesModal(false);
                                                    }}
                                                >
                                                    <i className="bi bi-box-arrow-up-right me-1"></i> Abrir
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {quotes.filter(q => (q.form.nombre || '').toLowerCase().includes(searchAllQuotes.toLowerCase()) || (q.form.cedula || '').includes(searchAllQuotes)).length === 0 && (
                                        <tr>
                                            <td colSpan="6" className="text-center text-muted py-4">No hay cotizaciones registradas en el sistema.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Offcanvas Drawer for Historial de Presupuestos */}
            {showDrawer && (
                <>
                    <div 
                        onClick={() => setShowDrawer(false)}
                        style={{
                            position: 'fixed',
                            inset: 0,
                            backgroundColor: 'rgba(15, 23, 42, 0.45)',
                            backdropFilter: 'blur(3px)',
                            zIndex: 1040,
                            transition: 'opacity 0.2s'
                        }}
                    />
                    <div 
                        style={{
                            position: 'fixed',
                            top: 0,
                            right: 0,
                            width: '420px',
                            maxWidth: '92vw',
                            height: '100vh',
                            backgroundColor: '#ffffff',
                            boxShadow: '-6px 0 25px rgba(0,0,0,0.15)',
                            zIndex: 1050,
                            display: 'flex',
                            flexDirection: 'column',
                            animation: 'slideInRight 0.25s cubic-bezier(0.16, 1, 0.3, 1)'
                        }}
                    >
                        {/* Drawer Header */}
                        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '800', color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <i className="bi bi-clock-history text-secondary"></i> Historial de Presupuestos
                                </h3>
                                <p style={{ margin: '3px 0 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
                                    {quotes.length} cotizaciones registradas
                                </p>
                            </div>
                            <button 
                                onClick={() => setShowDrawer(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    color: '#6b7280',
                                    padding: '4px 8px',
                                    lineHeight: 1
                                }}
                            >
                                &times;
                            </button>
                        </div>

                        {/* Drawer Quick Actions */}
                        <div style={{ padding: '1rem 1.5rem', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: '8px' }}>
                            <button 
                                className="btn btn-sm text-white" 
                                style={{ flex: 1, padding: '7px 10px', backgroundColor: '#111827', color: '#ffffff', borderRadius: '8px', fontWeight: '700', fontSize: '0.82rem' }} 
                                onClick={() => { handleNewQuote(); setShowDrawer(false); }}
                            >
                                <i className="bi bi-plus-lg me-1" style={{ color: '#ffffff' }}></i> <span style={{ color: '#ffffff' }}>Nuevo</span>
                            </button>
                            <button 
                                onClick={() => { setShowDrawer(false); setShowAllQuotesModal(true); }}
                                className="btn btn-sm btn-outline-dark"
                                style={{ flex: 1, padding: '7px 10px', borderRadius: '8px', fontWeight: '700', fontSize: '0.82rem' }}
                            >
                                <i className="bi bi-table me-1"></i> Ver Todos
                            </button>
                            <button 
                                onClick={handleRecoverQuotes}
                                disabled={recovering}
                                className="btn btn-sm btn-light border"
                                style={{ padding: '7px 10px', borderRadius: '8px', fontWeight: '700', fontSize: '0.82rem' }}
                                title="Recuperar cotizaciones antiguas"
                            >
                                {recovering ? '...' : <i className="bi bi-arrow-clockwise"></i>}
                            </button>
                        </div>

                        {/* Drawer Search */}
                        <div style={{ padding: '1rem 1.5rem 0.5rem 1.5rem' }}>
                            <div style={{ position: 'relative' }}>
                                <i className="bi bi-search" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}></i>
                                <input 
                                    type="text" 
                                    placeholder="Buscar por paciente o fecha..." 
                                    value={searchQuote}
                                    onChange={(e) => setSearchQuote(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px 8px 36px',
                                        borderRadius: '8px',
                                        border: '1px solid #d1d5db',
                                        fontSize: '0.85rem',
                                        outline: 'none'
                                    }}
                                />
                            </div>
                        </div>

                        {/* Drawer Quote List */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem 1.5rem 1rem 1.5rem' }}>
                            {quotes.filter(q => (q.form.nombre || '').toLowerCase().includes(searchQuote.toLowerCase()) || (q.date || '').includes(searchQuote)).map(q => (
                                <div 
                                    key={q.id} 
                                    style={{
                                        padding: '10px 14px',
                                        marginBottom: '8px',
                                        border: currentQuote?.id === q.id ? '1.5px solid #111827' : '1px solid #e5e7eb',
                                        backgroundColor: currentQuote?.id === q.id ? '#f3f4f6' : '#ffffff',
                                        borderRadius: '10px',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '4px'
                                    }}
                                    onClick={() => { handleSelectQuote(q); setShowDrawer(false); }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ fontWeight: '700', fontSize: '0.9rem', color: '#111827', wordBreak: 'break-word', flex: 1, paddingRight: '8px' }}>
                                            {q.form.nombre || 'Sin nombre'}
                                        </div>
                                        <span style={{
                                            fontSize: '0.65rem',
                                            padding: '2px 8px',
                                            borderRadius: '12px',
                                            fontWeight: '700',
                                            whiteSpace: 'nowrap',
                                            backgroundColor: q.status === 'Completada' ? '#d1fae5' : q.status === 'Anulada' ? '#fee2e2' : '#fef3c7',
                                            color: q.status === 'Completada' ? '#065f46' : q.status === 'Anulada' ? '#991b1b' : '#92400e',
                                            border: `1px solid ${q.status === 'Completada' ? '#a7f3d0' : q.status === 'Anulada' ? '#fecaca' : '#fde68a'}`
                                        }}>
                                            {q.status}
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.78rem', color: '#6b7280' }}>
                                        <span><i className="bi bi-calendar3 me-1"></i>{q.date}</span>
                                        {q.form.cedula && <span>C.I: {q.form.cedula}</span>}
                                    </div>
                                </div>
                            ))}
                            {quotes.filter(q => (q.form.nombre || '').toLowerCase().includes(searchQuote.toLowerCase()) || (q.date || '').includes(searchQuote)).length === 0 && (
                                <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#9ca3af' }}>
                                    <i className="bi bi-inbox" style={{ fontSize: '2.5rem', display: 'block', marginBottom: '8px', opacity: 0.6 }}></i>
                                    <p style={{ margin: 0, fontSize: '0.88rem' }}>No se encontraron cotizaciones</p>
                                </div>
                            )}
                        </div>

                        {/* Drawer Footer */}
                        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb', backgroundColor: '#f9fafb', display: 'flex', justifyContent: 'flex-end' }}>
                            <button 
                                onClick={() => setShowDrawer(false)}
                                className="btn btn-sm btn-light border"
                                style={{ borderRadius: '8px', fontWeight: '600', padding: '6px 16px' }}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Sticky Top Action Bar */}
            <div 
                className="d-print-none" 
                style={{ 
                    position: 'sticky', 
                    top: '12px', 
                    zIndex: 100, 
                    backgroundColor: '#ffffff', 
                    borderRadius: '14px', 
                    padding: '12px 20px', 
                    margin: '12px 24px 20px 24px', 
                    boxShadow: '0 4px 16px rgba(0,0,0,0.06)', 
                    border: '1px solid #e5e7eb', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    flexWrap: 'wrap', 
                    gap: '12px' 
                }}
            >
                {/* Left: Title & Status */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '800', color: '#111827', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        Generador de Presupuestos
                    </h2>
                    {currentQuote ? (
                        <span style={{ 
                            fontSize: '0.78rem', 
                            padding: '4px 12px', 
                            borderRadius: '20px', 
                            fontWeight: '700', 
                            backgroundColor: '#f1f5f9', 
                            color: '#334155', 
                            border: '1px solid #cbd5e1',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}>
                            <i className="bi bi-pencil-square text-primary"></i> Editando: {form.nombre || 'Sin nombre'}
                        </span>
                    ) : (
                        <span style={{ 
                            fontSize: '0.78rem', 
                            padding: '4px 12px', 
                            borderRadius: '20px', 
                            fontWeight: '700', 
                            backgroundColor: '#ecfdf5', 
                            color: '#065f46', 
                            border: '1px solid #a7f3d0',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}>
                            <i className="bi bi-file-earmark-plus text-success"></i> Nuevo Presupuesto
                        </span>
                    )}
                </div>

                {/* Right: Actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <button 
                        onClick={() => setShowDrawer(true)}
                        className="btn btn-sm"
                        style={{ 
                            backgroundColor: '#f8fafc', 
                            border: '1.5px solid #d1d5db', 
                            color: '#111827', 
                            fontWeight: '700', 
                            borderRadius: '8px', 
                            padding: '7px 14px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                        title="Abrir historial de presupuestos"
                    >
                        <i className="bi bi-clock-history"></i> Historial ({quotes.length})
                    </button>

                    <button 
                        onClick={handleNewQuote}
                        className="btn btn-sm"
                        style={{ 
                            backgroundColor: '#ffffff', 
                            border: '1.5px solid #d1d5db', 
                            color: '#374151', 
                            fontWeight: '700', 
                            borderRadius: '8px', 
                            padding: '7px 14px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                        title="Crear nuevo presupuesto en blanco"
                    >
                        <i className="bi bi-plus-lg"></i> Nuevo
                    </button>

                    <button 
                        style={{ 
                            ...styles.buttonPrimary, 
                            padding: '7px 18px', 
                            borderRadius: '8px', 
                            fontSize: '0.9rem',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            color: '#ffffff',
                            boxShadow: '0 2px 4px rgba(17,24,39,0.15)'
                        }} 
                        onClick={handleSaveCurrent}
                    >
                        <i className="bi bi-save" style={{ color: '#ffffff' }}></i> <span style={{ color: '#ffffff' }}>Guardar</span>
                    </button>

                    <button 
                        style={{ 
                            ...styles.buttonSuccess, 
                            padding: '7px 16px', 
                            borderRadius: '8px', 
                            fontSize: '0.9rem',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            backgroundColor: '#ecfdf5'
                        }} 
                        onClick={handlePrint}
                    >
                        <i className="bi bi-printer"></i> Imprimir / PDF
                    </button>

                    <a 
                        href={`/clinica/pacientes${currentQuote ? `?quote_id=${currentQuote.id}&patient_id=${currentQuote.db_patient_id || ''}` : ''}`}
                        target="_blank" 
                        rel="noopener noreferrer" 
                        style={{ 
                            ...styles.buttonPrimary, 
                            backgroundColor: '#374151', 
                            borderColor: '#374151', 
                            textDecoration: 'none', 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: '6px',
                            padding: '7px 14px',
                            borderRadius: '8px',
                            fontSize: '0.9rem'
                        }}
                        title="Abre la ficha clínica del paciente para completar el historial médico detallado."
                    >
                        <i className="bi bi-person-lines-fill"></i> Completar Ficha
                    </a>

                    <button
                        style={{ ...styles.buttonPrimary, backgroundColor: '#b59a4a', borderColor: '#b59a4a', padding: '7px 14px', borderRadius: '8px', fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                        onClick={() => {
                            if (!itemsParaFactura().length) {
                                setSaveModal({ show: true, message: 'Agregue al menos un producto con precio para facturar.', type: 'error' });
                                return;
                            }
                            setMostrarFactura(true);
                        }}
                        title="Emitir factura electrónica (SRI) de este presupuesto"
                    >
                        <i className="bi bi-receipt-cutoff" style={{ color: '#fff' }}></i> <span style={{ color: '#fff' }}>Facturar</span>
                    </button>

                    {currentQuote && !currentQuote.anulada && (
                        <button
                            style={{ ...styles.buttonDanger, padding: '7px 12px', borderRadius: '8px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                            onClick={handleAnularQuote}
                            title="Anular este presupuesto (no se borra; queda marcado como ANULADO)"
                        >
                            <i className="bi bi-slash-circle"></i> Anular
                        </button>
                    )}
                    {currentQuote?.anulada && (
                        <span title={`Motivo: ${currentQuote.motivoAnulacion || '—'}`} style={{ fontSize: '0.78rem', padding: '5px 12px', borderRadius: '999px', fontWeight: 700, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>
                            <i className="bi bi-slash-circle me-1"></i>ANULADO
                        </span>
                    )}

                </div>
            </div>

            {mostrarFactura && (
                <ModalFactura
                    paciente={pacienteParaFactura()}
                    items={itemsParaFactura()}
                    origen="presupuesto"
                    origenId={currentQuote?.id || ''}
                    onClose={() => setMostrarFactura(false)}
                />
            )}

            <style>
                {`
                    @keyframes slideInRight {
                        from { transform: translateX(100%); }
                        to { transform: translateX(0); }
                    }
                    @media print {
                        body * { visibility: hidden !important; }
                        #print-quote-area, #print-quote-area * { visibility: visible !important; }
                        #print-quote-area { 
                            position: absolute !important; 
                            left: 0 !important; 
                            top: 0 !important; 
                            width: 210mm !important; 
                            margin: 0 !important; 
                            padding: 8mm !important; 
                            box-shadow: none !important; 
                            border: none !important; 
                            border-radius: 0 !important;
                        }
                    }
                    .pill-table { width: 100%; border-collapse: separate; border-spacing: 0 4px; font-size: 11px; text-align: center; }
                    .pill-table th { padding: 3px; font-weight: 900; }
                    .pill-table td { background-color: #f3f4f6; padding: 2px; height: 22px; border-left: 2px solid white; border-right: 2px solid white; }
                    .pill-table td:first-child { border-top-left-radius: 20px; border-bottom-left-radius: 20px; border-left: none; }
                    .pill-table td:last-child { border-top-right-radius: 20px; border-bottom-right-radius: 20px; border-right: none; }
                    .transparent-input { background: transparent; border: none; width: 100%; outline: none; font-weight: bold; color: #000; }
                `}
            </style>

            {/* Document Canvas Container */}
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '0 16px 60px 16px', overflowX: 'auto' }}>
                <div id="print-quote-area" ref={printRef} style={{ width: '100%', maxWidth: '850px', minHeight: '270mm', backgroundColor: 'white', margin: '0 auto', padding: '10mm 12mm', boxSizing: 'border-box', position: 'relative', fontFamily: "'Segoe UI', Roboto, sans-serif", boxShadow: '0 4px 20px rgba(0,0,0,0.07)', borderRadius: '12px', border: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column' }}>
                
                <HojaEstilos />
                <HojaDatalists />
                <HojaEncabezado titulo="PRESUPUESTO & RECETA ÓPTICA" style={{ paddingLeft: 0 }} />

                {/* Datos del paciente (formato de la hoja impresa) */}
                <div className="hv-frame" style={{ marginBottom: '12px' }}>
                    <div className="hv-row2">
                        <Linea label="NOMBRES Y APELLIDOS:" name="nombre" value={form.nombre} onChange={handleChangeForm} inputStyle={{ fontWeight: 700 }} />
                        <Linea label="C.I.:" name="cedula" value={form.cedula} onChange={handleChangeForm} />
                    </div>
                    <div className="hv-row2">
                        <Linea label="REPRESENTANTE:" name="representante" value={form.representante} onChange={handleChangeForm} />
                        <Linea label="C.I. REP.:" name="cedulaRepresentante" value={form.cedulaRepresentante} onChange={handleChangeForm} />
                    </div>
                    <div className="hv-row2">
                        <Linea label="DIRECCIÓN:" name="direccion" value={form.direccion} onChange={handleChangeForm} />
                        <Linea label="FECHA NAC.:" name="fechaNacimiento" type="date" value={form.fechaNacimiento} onChange={handleChangeForm} />
                    </div>
                    <div className="hv-row2">
                        <Linea label="TELÉFONO:" name="telefono" value={form.telefono} onChange={handleChangeForm} />
                        <Linea label="OCUPACIÓN:" name="ocupacion" value={form.ocupacion} onChange={handleChangeForm} />
                    </div>
                    <Linea label="CORREO:" name="correo" value={form.correo} onChange={handleChangeForm} style={{ width: '62%', marginBottom: 0 }} />
                </div>

                {/* RX. FINAL + especificaciones de lentes, igual que en la Historia Clínica Visual */}
                <ErroresHoja.Provider value={intentoGuardarRx ? validarFormula(rxData).campos : {}}>
                <AvisoErrores errores={intentoGuardarRx ? validarFormula(rxData).campos : {}} />
                <div className="hv-frame" style={{ marginBottom: '14px', position: 'relative' }} onBlur={handleRxBlur}>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-dark d-print-none"
                        data-html2canvas-ignore="true"
                        onClick={handleTraerRxUltimoExamen}
                        disabled={loadingRx}
                        style={{ position: 'absolute', top: '4px', right: '8px', fontSize: '11px', padding: '2px 10px', borderRadius: '6px', fontWeight: 600 }}
                    >
                        <i className="bi bi-arrow-down-circle me-1"></i>{loadingRx ? 'Buscando...' : 'Traer RX del último examen'}
                    </button>
                    <RxFinalTabla data={rxData} onChange={handleRxChange} />
                    <EspecificacionesLentes data={rxData} onChange={handleRxChange} />
                </div>
                </ErroresHoja.Provider>

                {/* Table */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                    <h3 style={{ margin: 0, fontSize: '13px', fontWeight: '900', letterSpacing: '0.5px' }}>DETALLE DE PRODUCTOS & SERVICIOS ÓPTICOS</h3>
                    <div className="d-print-none">
                        <button className="btn btn-sm btn-dark me-2 d-inline-flex align-items-center gap-1" onClick={addTreatmentRow} title="Añadir Fila" style={{ borderRadius: '6px', fontWeight: 'bold' }}>
                            <i className="bi bi-plus-lg"></i> Añadir Fila
                        </button>
                    </div>
                </div>
                <table className="pill-table">
                    <thead>
                        <tr>
                            <th style={{ width: '8%' }}>N°</th>
                            <th style={{ width: '42%' }}>PRODUCTO / SERVICIO</th>
                            <th style={{ width: '15%' }}>CANTIDAD</th>
                            <th style={{ width: '15%' }}>PRECIO UNIT.</th>
                            <th style={{ width: '20%', position: 'relative' }}>
                                TOTAL
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {treatments.map((t, i) => (
                            <tr key={i} style={{ position: 'relative' }}>
                                <td>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontWeight: 'bold' }}>
                                        {i + 1}
                                    </div>
                                </td>
                                <td>
                                    <TermAutocomplete
                                        value={t.procedimiento}
                                        termsList={catalogOptions}
                                        onUpdate={(val) => handleTreatmentChange(i, 'procedimiento', val)}
                                        onSelectFullItem={(item) => {
                                            const newTreatments = [...treatments];
                                            newTreatments[i]['procedimiento'] = item.code;
                                            newTreatments[i]['precio'] = item.price !== undefined ? item.price : 0;
                                            newTreatments[i]['total_final'] = (item.price !== undefined ? item.price : 0) * (parseInt(newTreatments[i].cantidad) || 1);
                                            setTreatments(newTreatments);
                                        }}
                                        insertField="code"
                                        placeholder="Buscar armazón, luna, servicio o procedimiento..."
                                        openUp={true}
                                        style={{ backgroundColor: 'transparent', border: 'none', textAlign: 'center', width: '100%', outline: 'none' }}
                                    />
                                </td>
                                <td>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <input type="number" min="1" value={t.cantidad !== undefined && t.cantidad !== '' ? t.cantidad : ''} onChange={e => handleTreatmentChange(i, 'cantidad', e.target.value)} onBlur={e => { if(e.target.value === '') handleTreatmentChange(i, 'cantidad', 1) }} className="transparent-input text-center" style={{ width: '100%', backgroundColor: 'transparent', border: 'none', outline: 'none', boxShadow: 'none' }} />
                                    </div>
                                </td>
                                <td>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                                        <span style={{marginRight: '5px'}}>$</span> <input type="number" min="0" step="0.01" value={t.precio} onChange={e => handleTreatmentChange(i, 'precio', e.target.value)} className="transparent-input text-center" style={{ width: '80px', backgroundColor: 'transparent', border: 'none', outline: 'none', boxShadow: 'none' }} />
                                    </div>
                                </td>
                                <td>
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', width: '100%', height: '100%' }}>
                                        <span style={{marginRight: '5px'}}>$</span> <input type="number" min="0" step="0.01" value={t.total_final !== undefined ? t.total_final : ((parseFloat(t.precio) || 0) * (parseInt(t.cantidad) || 1))} onChange={e => handleTreatmentChange(i, 'total_final', e.target.value)} className="transparent-input text-center" style={{ width: '80px', backgroundColor: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', fontWeight: 'bold' }} />
                                        <button className="btn btn-sm text-danger d-print-none" style={{ position: 'absolute', right: '-20px', padding: '0 5px' }} onClick={() => removeTreatmentRow(i)}>
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {/* Rellenar filas vacías para diseño */}
                        {[...Array(Math.max(0, 4 - treatments.length))].map((_, i) => (
                            <tr key={`empty-${i}`}>
                                <td></td><td></td><td></td><td></td><td></td>
                            </tr>
                        ))}
                        <tr style={{ backgroundColor: 'transparent' }}>
                            <td colSpan="3" style={{ backgroundColor: 'transparent', paddingRight: '15px' }}>
                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <div style={{ width: '200px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', fontWeight: '900', fontSize: '14px' }}>
                                        <span>SUBTOTAL</span>
                                    </div>
                                </div>
                            </td>
                            <td colSpan="2" style={{ fontWeight: '900', fontSize: '14px', backgroundColor: 'transparent', textAlign: 'center' }}>${subtotalCost.toFixed(2)}</td>
                        </tr>
                        
                        <tr style={{ backgroundColor: 'transparent' }}>
                            <td colSpan="3" style={{ backgroundColor: 'transparent', paddingRight: '15px' }}>
                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <div style={{ width: '200px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontWeight: '900', fontSize: '14px' }}>
                                        <span>DESCUENTO</span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                            <input 
                                                type="number" 
                                                min="0" 
                                                max="100" 
                                                value={form.discountPercentage || 0} 
                                                onChange={(e) => setForm({...form, discountPercentage: e.target.value})} 
                                                className="transparent-input text-center d-print-none"
                                                style={{ width: '40px', backgroundColor: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', fontWeight: 'bold', borderBottom: '1px solid #ccc', padding: '0' }} 
                                            />
                                            <span className="d-print-none">%</span>
                                            {form.discountPercentage > 0 && <span className="d-none d-print-inline">{form.discountPercentage}%</span>}
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td colSpan="2" style={{ fontWeight: '900', fontSize: '14px', backgroundColor: 'transparent', textAlign: 'center' }}>
                                ${globalDiscountAmount.toFixed(2)}
                            </td>
                        </tr>
                        
                        <tr style={{ backgroundColor: 'transparent' }}>
                            <td colSpan="3" style={{ backgroundColor: 'transparent', paddingRight: '15px' }}>
                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <div style={{ width: '200px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', fontWeight: '900', fontSize: '14px' }}>
                                        <span>TOTAL A PAGAR</span>
                                    </div>
                                </div>
                            </td>
                            <td colSpan="2" style={{ fontWeight: '900', fontSize: '14px', backgroundColor: 'transparent', textAlign: 'center' }}>${finalTotalCost.toFixed(2)}</td>
                        </tr>
                    </tbody>
                </table>

                {/* Footer */}
                <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'center', backgroundColor: '#e5e7eb', borderRadius: '30px', padding: '8px 20px', alignItems: 'center' }}>
                    <div style={{ fontSize: '10px', fontWeight: 'bold', display: 'flex', gap: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#374151' }}>
                            <i className="bi bi-whatsapp"></i> CONTÁCTANOS: 0962602999
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#374151' }}>
                            <i className="bi bi-geo-alt-fill"></i> DIRECCIÓN: ENTRE ARELLANO Y AYACUCHO
                        </div>
                    </div>
                </div>

            </div>
        </div>
        </div>
    );
};

export default Consultas;
